import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Link2, Database, Users, FileText, Cloud, Lock, MessageSquare,
  CheckCircle, Clock, ArrowRight, Sparkles, Zap, Globe, TrendingUp, Loader2, AlertTriangle
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import MicrosoftSharePointDialog from '@/components/MicrosoftSharePointDialog';
import SharePointLibraryDialog from '@/components/SharePointLibraryDialog';
import ConnectorConfigDialog from '@/components/ConnectorConfigDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { PageShell } from '@/components/PageShell';
import { useAuth } from '@/contexts/AuthContext';

// Brand Logo Components (SVG icons)
const MicrosoftTeamsLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <path d="M20.625 6.75H14.25V3.375C14.25 2.339 15.089 1.5 16.125 1.5H18.75C19.786 1.5 20.625 2.339 20.625 3.375V6.75Z" fill="#5059C9"/>
    <path d="M14.25 6.75V17.25C14.25 18.907 12.907 20.25 11.25 20.25H3.75C2.093 20.25 0.75 18.907 0.75 17.25V9.75C0.75 8.093 2.093 6.75 3.75 6.75H14.25Z" fill="#7B83EB"/>
    <path d="M14.25 6.75H20.625V17.25C20.625 18.286 19.786 19.125 18.75 19.125H14.25V6.75Z" fill="#5059C9"/>
    <circle cx="17.625" cy="4.125" r="2.625" fill="#7B83EB"/>
    <circle cx="9" cy="13.5" r="3" fill="white"/>
  </svg>
);

const MicrosoftEntraLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" fill="#0078D4"/>
    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#50E6FF"/>
    <path d="M12 12V22L22 17V7L12 12Z" fill="#0078D4"/>
    <path d="M2 7V17L12 22V12L2 7Z" fill="#0050C8"/>
    <circle cx="12" cy="10" r="3" fill="white"/>
    <path d="M8 16C8 14 9.5 13 12 13C14.5 13 16 14 16 16" stroke="white" strokeWidth="1.5" fill="none"/>
  </svg>
);

const OktaLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <circle cx="12" cy="12" r="10" fill="#007DC1"/>
    <circle cx="12" cy="12" r="5" fill="white"/>
  </svg>
);

const GoogleWorkspaceLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const SlackLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A"/>
    <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0"/>
    <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D"/>
    <path d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#ECB22E"/>
  </svg>
);

const SharePointLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <circle cx="9" cy="9" r="7" fill="#036C70"/>
    <circle cx="15" cy="12" r="6" fill="#1A9BA1"/>
    <circle cx="11" cy="16" r="5" fill="#37C6D0"/>
    <path d="M9 6L7 9L9 12L11 9L9 6Z" fill="white"/>
  </svg>
);

const GoogleDriveLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M8.267 14.68l-1.6 2.773H20.4l1.6-2.773z" fill="#3777E3"/>
    <path d="M15.867 3H8.267l-6.4 11.093h7.6z" fill="#FFCF63"/>
    <path d="M22 14.093L15.867 3l-6.134 11.093 1.6 2.773 4.534-7.866z" fill="#11A861"/>
  </svg>
);

const ConfluenceLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M2.073 17.663c-.282.47-.133 1.078.33 1.365l5.856 3.581c.466.287 1.08.142 1.364-.326l.64-1.06c1.954-3.232 3.97-3.232 7.737-.266l3.127 2.463c.417.33 1.027.26 1.358-.156l2.856-3.568c.331-.417.262-1.024-.155-1.354l-3.126-2.463c-6.57-5.172-11.97-4.1-16.46 3.39l-3.527-2.605z" fill="#2684FF"/>
    <path d="M21.927 6.337c.282-.47.133-1.078-.33-1.365L15.741 1.39c-.466-.287-1.08-.142-1.364.326l-.64 1.06c-1.954 3.232-3.97 3.232-7.737.266L2.873.579c-.417-.33-1.027-.26-1.358.156L-1.341 4.303c-.331.417-.262 1.024.155 1.354l3.126 2.463c6.57 5.172 11.97 4.1 16.46-3.39l3.527 2.607z" fill="#2684FF"/>
  </svg>
);

const BoxLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0061D5"/>
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#4D9AFF"/>
    <path d="M12 12v10l10-5V7l-10 5z" fill="#0061D5"/>
    <path d="M2 7v10l10 5V12L2 7z" fill="#003D8A"/>
  </svg>
);

const NotionLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466l1.823 1.447zm.793 3.08v13.904c0 .747.373 1.027 1.213.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.886l-15.177.887c-.56.047-.746.327-.746.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.886.747-.933l3.222-.187zM2.618 1.22l13.029-.934c1.635-.14 2.055-.047 3.082.7l4.25 2.986c.7.513.933.653.933 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.948c0-.84.374-1.54 1.728-1.727z" fill="currentColor"/>
  </svg>
);

const WorkdayLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#F68D2E"/>
    <path d="M7 12C7 9.239 9.239 7 12 7C14.761 7 17 9.239 17 12" stroke="white" strokeWidth="2" fill="none"/>
    <circle cx="12" cy="12" r="2" fill="white"/>
  </svg>
);

const BambooHRLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#73C41D"/>
    <path d="M12 4C12 4 8 8 8 12C8 16 10 18 12 20C14 18 16 16 16 12C16 8 12 4 12 4Z" fill="white"/>
  </svg>
);

const SAPLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="6" width="20" height="12" rx="2" fill="#0FAAFF"/>
    <text x="12" y="14" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">SAP</text>
  </svg>
);

const ADPLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="2" fill="#D0271D"/>
    <text x="12" y="14" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">ADP</text>
  </svg>
);

const UKGLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="2" fill="#005CB9"/>
    <text x="12" y="14" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">UKG</text>
  </svg>
);

const OracleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="6" width="20" height="12" rx="2" fill="#F80000"/>
    <text x="12" y="14" textAnchor="middle" fill="white" fontSize="4" fontWeight="bold">ORACLE</text>
  </svg>
);

const CopilotStudioLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#6264A7"/>
    <circle cx="8" cy="10" r="2" fill="white"/>
    <circle cx="16" cy="10" r="2" fill="white"/>
    <path d="M8 15C8 15 10 18 12 18C14 18 16 15 16 15" stroke="white" strokeWidth="1.5" fill="none"/>
  </svg>
);

const FirestoreLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path d="M4.889 8.04L12.14 2l2.422 4.038L4.889 8.04z" fill="#FFC24A"/>
    <path d="M4.889 8.04l2.795-2.337 2.077 1.335-4.872 1.002z" fill="#FFA712"/>
    <path d="M12.14 22l7.25-4.038-4.828-11.924L12.14 22z" fill="#F4BD62"/>
    <path d="M12.14 22l-7.251-4.038 4.872-9.922L12.14 22z" fill="#FFA50E"/>
    <path d="M19.39 17.962L12.14 2l2.422 4.038 4.828 11.924z" fill="#F6820C"/>
    <path d="M4.889 8.04l4.872 9.922L12.14 2 4.889 8.04z" fill="#FDE068"/>
  </svg>
);

const CosmosDBLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <ellipse cx="12" cy="12" rx="10" ry="4" fill="#0078D4"/>
    <ellipse cx="12" cy="12" rx="10" ry="4" fill="none" stroke="#50E6FF" strokeWidth="1" transform="rotate(60 12 12)"/>
    <ellipse cx="12" cy="12" rx="10" ry="4" fill="none" stroke="#50E6FF" strokeWidth="1" transform="rotate(-60 12 12)"/>
    <circle cx="12" cy="12" r="2" fill="#50E6FF"/>
  </svg>
);

const PingIdentityLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#B3282D"/>
    <circle cx="12" cy="12" r="4" fill="white"/>
  </svg>
);

const WebLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <circle cx="12" cy="12" r="10" stroke="#6366F1" strokeWidth="2" fill="none"/>
    <ellipse cx="12" cy="12" rx="4" ry="10" stroke="#6366F1" strokeWidth="2" fill="none"/>
    <line x1="2" y1="12" x2="22" y2="12" stroke="#6366F1" strokeWidth="2"/>
  </svg>
);

const EvaPeopleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#7C3AED"/>
    <path d="M7 8h10M7 12h6M7 16h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="17" cy="16" r="2" fill="#A78BFA"/>
  </svg>
);

// Logo mapping
const LOGO_COMPONENTS = {
  'Microsoft Teams': MicrosoftTeamsLogo,
  'Microsoft Copilot Studio': CopilotStudioLogo,
  'Microsoft Entra ID': MicrosoftEntraLogo,
  'Okta': OktaLogo,
  'Google Workspace': GoogleWorkspaceLogo,
  'Slack': SlackLogo,
  'SharePoint': SharePointLogo,
  'Google Drive': GoogleDriveLogo,
  'Confluence': ConfluenceLogo,
  'Box': BoxLogo,
  'Notion': NotionLogo,
  'Workday': WorkdayLogo,
  'Eva People': EvaPeopleLogo,
  'Eva People for Web': EvaPeopleLogo,
  'Eva People for Teams': MicrosoftTeamsLogo,
  'Eva People for Slack': SlackLogo,
  'BambooHR': BambooHRLogo,
  'SAP SuccessFactors': SAPLogo,
  'ADP Workforce Now': ADPLogo,
  'UKG Pro': UKGLogo,
  'Oracle HCM': OracleLogo,
  'Google Firestore': FirestoreLogo,
  'Azure Cosmos DB': CosmosDBLogo,
  'Ping Identity': PingIdentityLogo,
  'Web': WebLogo,
};

const CONNECTOR_CATEGORIES = [
  {
    title: 'Knowledge Sources',
    description: 'Connect document repositories to power Eva\'s knowledge base',
    icon: FileText,
    connectors: [
      { 
        name: 'SharePoint', 
        description: 'Microsoft SharePoint document libraries',
        status: 'coming_soon',
        features: ['Select files to import as drafts', 'Choose Eva access before publication', 'Permission mirroring planned']
      },
      { 
        name: 'Google Drive', 
        description: 'Google Workspace document storage',
        status: 'coming_soon',
        features: ['Folder sync', 'Shared drives', 'Real-time updates']
      },
      { 
        name: 'Confluence', 
        description: 'Atlassian Confluence wikis and spaces',
        status: 'coming_soon',
        features: ['Space sync', 'Page hierarchy', 'Comment extraction']
      },
      { 
        name: 'Box', 
        description: 'Box cloud content management',
        status: 'coming_soon',
        features: ['Enterprise sync', 'Metadata mapping', 'Access controls']
      },
      { 
        name: 'Notion', 
        description: 'Notion workspace pages and databases',
        status: 'coming_soon',
        features: ['Page sync', 'Database integration', 'Block parsing']
      },
    ]
  },
  {
    title: 'HRIS Systems',
    description: 'Connect HR systems for personalized employee experiences',
    icon: Users,
    connectors: [
      { 
        name: 'Workday', 
        description: 'Enterprise HR and finance platform',
        status: 'coming_soon',
        features: ['Employee profiles', 'Org hierarchy', 'Benefits data', 'Time off balances']
      },
      { 
        name: 'BambooHR', 
        description: 'SMB HR management platform',
        status: 'coming_soon',
        features: ['Employee directory', 'PTO tracking', 'Performance data']
      },
      { 
        name: 'SAP SuccessFactors', 
        description: 'SAP cloud HCM suite',
        status: 'coming_soon',
        features: ['Core HR', 'Talent management', 'Learning data']
      },
      { 
        name: 'ADP Workforce Now', 
        description: 'ADP payroll and HR platform',
        status: 'coming_soon',
        features: ['Payroll data', 'Benefits enrollment', 'Time tracking']
      },
      { 
        name: 'UKG Pro', 
        description: 'Ultimate Kronos Group HCM',
        status: 'coming_soon',
        features: ['Workforce management', 'HR service delivery', 'Payroll']
      },
      { 
        name: 'Oracle HCM', 
        description: 'Oracle Human Capital Management',
        status: 'coming_soon',
        features: ['Global HR', 'Talent acquisition', 'Workforce planning']
      },
    ]
  },
  {
    title: 'Identity & Access',
    description: 'Sync user identity for personalized and secure access',
    icon: Lock,
    connectors: [
      { 
        name: 'Microsoft Entra ID', 
        description: 'Azure Active Directory identity',
        status: 'coming_soon',
        features: ['SSO authentication', 'Group sync', 'User provisioning']
      },
      { 
        name: 'Okta', 
        description: 'Enterprise identity management',
        status: 'coming_soon',
        features: ['SAML/OIDC', 'Directory sync', 'MFA integration']
      },
      { 
        name: 'Google Workspace', 
        description: 'Google identity and directory',
        status: 'coming_soon',
        features: ['Google SSO', 'Groups sync', 'Domain verification']
      },
      { 
        name: 'Ping Identity', 
        description: 'Enterprise identity security',
        status: 'coming_soon',
        features: ['Federated SSO', 'API security', 'Directory integration']
      },
    ]
  },
  {
    title: 'Communication Platforms',
    description: 'Where Eva interacts with employees',
    icon: Cloud,
    connectors: [
      { 
        name: 'Eva People for Teams', 
        description: 'Eva People deployed in Microsoft Teams',
        status: 'coming_soon',
        features: ['Teams bot integration', 'Adaptive cards', 'Channel notifications']
      },
      { 
        name: 'Eva People for Slack', 
        description: 'Eva People in your Slack workspace',
        status: 'coming_soon',
        features: ['Slack bot integration', 'Channel posting', 'Slash commands']
      },
      { 
        name: 'Eva People for Web', 
        description: 'Employee web chat interface',
        status: 'available',
        features: ['Web chat interface', 'Custom branding', 'Multi-language support', 'Session summarization']
      },
    ]
  },
];

function ConnectorCard({ connector, onConfigure, configuredConnectors: _configuredConnectors, evaPeopleConfig, onEvaPeopleToggle, voteCount, userVoted, onVote, votingConnector }) {
  const LogoComponent = LOGO_COMPONENTS[connector.name];
  const isEvaPeopleForWeb = connector.name === 'Eva People for Web';
  const isEvaPeopleEnabled = isEvaPeopleForWeb ? evaPeopleConfig?.enabled ?? true : false;
  const isVoting = votingConnector === connector.name;

  return (
    <div
      className={`bg-card border rounded-xl p-5 transition-all flex flex-col ${
        isEvaPeopleForWeb && isEvaPeopleEnabled ? 'border-success/30 shadow-sm' : 'border-border'
      }`}
      data-testid={`connector-${connector.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header row: logo + name + badge */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
            {LogoComponent ? <LogoComponent /> : <Globe className="w-6 h-6 text-muted-foreground" />}
          </div>
          <div>
            <h4 className="font-semibold text-foreground text-sm">{connector.name}</h4>
            <p className="text-xs text-muted-foreground">{connector.description}</p>
          </div>
        </div>
        {/* Badge — Eva People for Web shows live status; everything else always Coming Soon */}
        {isEvaPeopleForWeb ? (
          <Badge className={isEvaPeopleEnabled
            ? 'bg-success/10 text-success border-0 shrink-0'
            : 'bg-muted text-muted-foreground border-0 shrink-0'
          }>
            {isEvaPeopleEnabled
              ? <><CheckCircle className="w-3 h-3 mr-1" />Enabled</>
              : <><Clock className="w-3 h-3 mr-1" />Disabled</>
            }
          </Badge>
        ) : connector.name === 'SharePoint' ? (
          <Badge variant="secondary">Manual import</Badge>
        ) : (
          <Badge variant="secondary" className="bg-orange-50 text-orange-500 border-orange-200 shrink-0 dark:bg-orange-500/10 dark:border-orange-500/20">
            <Clock className="w-3 h-3 mr-1" />
            Coming Soon
          </Badge>
        )}
      </div>

      {/* Features list */}
      <div className="space-y-1.5 mb-4 flex-1">
        {connector.features.map((feature, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isEvaPeopleForWeb && isEvaPeopleEnabled ? 'bg-success' : 'bg-muted'}`} />
            {feature}
          </div>
        ))}
      </div>

      {/* Footer — Eva People gets switch + configure; everything else gets only Upvote */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        {isEvaPeopleForWeb ? (
          <>
            <Switch
              checked={isEvaPeopleEnabled}
              onCheckedChange={(checked) => onEvaPeopleToggle && onEvaPeopleToggle(checked)}
              data-testid="eva-people-toggle"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => onConfigure('eva_people', null)}
              data-testid="eva-people-configure-btn"
            >
              Configure
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </>
        ) : connector.name === 'SharePoint' ? (
          <Button variant="outline" onClick={() => onConfigure('sharepoint', null)}>Add connection</Button>
        ) : (
          <button
            onClick={() => !isVoting && onVote && onVote(connector.name)}
            disabled={isVoting}
            className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
              userVoted
                ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted hover:text-foreground'
            } ${isVoting ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            data-testid={`vote-${connector.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <TrendingUp className={`w-4 h-4 ${userVoted ? 'text-primary' : ''}`} />
            <span>{userVoted ? 'Voted' : 'Upvote'}</span>
            {voteCount > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${userVoted ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {voteCount}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ConnectorsPage() {
  const { hasRole } = useAuth();
  const [configuredConnectors, setConfiguredConnectors] = useState([]);
  const [microsoftDialog, setMicrosoftDialog] = useState(null);
  const [libraryConnector, setLibraryConnector] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedConnectorType, setSelectedConnectorType] = useState(null);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [, setLoading] = useState(true);
  const [evaPeopleConfig, setEvaPeopleConfig] = useState(null);
  const [connectorVotes, setConnectorVotes] = useState([]);
  const [userVoted, setUserVoted] = useState([]);
  const [votingConnector, setVotingConnector] = useState(null);
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const isAdmin = hasRole(['admin']);

  // Fetch configured connectors
  const fetchConnectors = async () => {
    try {
      const [connResponse, votesResponse] = await Promise.allSettled([
        api.get('/connectors'),
        api.get('/connectors/votes/all'),
      ]);
      if (connResponse.status === 'fulfilled') {
        const connections = connResponse.value.data.connectors || [];
        setConfiguredConnectors(connections);
        const pending = new URLSearchParams(window.location.search).get('microsoft_connection');
        if (pending) {
          window.history.replaceState(null, '', window.location.pathname);
          const connection = connections.find(c => c.id === pending && c.auth_mode === 'delegated');
          if (connection) setMicrosoftDialog({ connector: connection });
        }
        try {
          const evaPeopleRes = await api.get('/connectors/eva-people/status');
          setEvaPeopleConfig(evaPeopleRes.data);
        } catch (_e) {
          setEvaPeopleConfig({ enabled: true, configured: false });
        }
      }
      if (votesResponse.status === 'fulfilled') {
        setConnectorVotes(votesResponse.value.data.votes || []);
        setUserVoted(votesResponse.value.data.user_voted || []);
      }
    } catch (err) {
      console.error('Failed to fetch connectors:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectors();
  }, []);

  if (!isAdmin) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Card className="bg-warning/5 border-warning/30">
            <CardContent className="p-6 flex items-center gap-4">
              <AlertTriangle className="h-8 w-8 text-warning" />
              <div>
                <h3 className="font-semibold">Access Denied</h3>
                <p className="text-muted-foreground">Only administrators can access connectors.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleVote = async (connectorName) => {
    setVotingConnector(connectorName);
    try {
      const res = await api.post(`/connectors/votes/${encodeURIComponent(connectorName)}`);
      if (res.data.voted) {
        setUserVoted(v => [...v, connectorName]);
      } else {
        setUserVoted(v => v.filter(n => n !== connectorName));
      }
      setConnectorVotes(prev => {
        const existing = prev.find(v => v.connector_name === connectorName);
        if (existing) {
          return prev.map(v => v.connector_name === connectorName ? { ...v, vote_count: res.data.vote_count } : v);
        }
        return [...prev, { connector_name: connectorName, vote_count: res.data.vote_count }];
      });
      toast.success(res.data.voted ? 'Vote recorded!' : 'Vote removed', { description: `${connectorName}: ${res.data.vote_count} votes` });
    } catch (_e) {
      toast.error('Failed to vote');
    } finally {
      setVotingConnector(null);
    }
  };

  const handleConfigure = async (connectorType, existingConnector = null) => {
    if (connectorType === 'eva_people') {
      setWelcomeDialogOpen(true);
      return;
    }
    if (connectorType === 'sharepoint' && !existingConnector) {
      setMicrosoftDialog({});
      return;
    }
    if (existingConnector?.auth_mode === 'delegated') {
      setMicrosoftDialog({ connector: existingConnector });
      return;
    }
    let details = existingConnector;
    if (existingConnector) {
      try {
        details = (await api.get(`/connectors/${existingConnector.id}`)).data;
      } catch {
        toast.error('Unable to load connector configuration');
        return;
      }
    }
    setSelectedConnectorType(connectorType);
    setSelectedConnector(details);
    setConfigDialogOpen(true);
  };

  const handleEvaPeopleToggle = async (enabled) => {
    try {
      await api.put('/connectors/eva-people/config', {
        ...evaPeopleConfig?.config,
        enabled
      });
      setEvaPeopleConfig(prev => ({ ...prev, enabled }));
      if (enabled) {
        toast.success('Eva People Enabled', { description: 'Employees can now access the Eva People chat interface.' });
      } else {
        toast.error('Eva People Disabled', { description: 'Employees will no longer be able to access Eva People.' });
      }
    } catch (_err) {
      toast.error('Error', { description: 'Failed to update Eva People settings' });
    }
  };

  const handleConfigSuccess = () => {
    fetchConnectors();
    toast.success('Connector saved', { description: 'Your connector has been configured.' });
  };

  // Compute dynamic summary counts
  const isEvaPeopleActive = evaPeopleConfig?.enabled ?? false;
  const activeCount = isEvaPeopleActive ? 1 : 0;
  
  const totalCount = CONNECTOR_CATEGORIES.reduce(
    (acc, cat) => acc + cat.connectors.length, 0
  );
  
  // Count connected identity providers 
  const identityProviderNames = ['Microsoft Entra ID', 'Okta', 'Google Workspace', 'Ping Identity'];
  const connectedIdentity = configuredConnectors.filter(
    c => identityProviderNames.includes(c.name) && c.status === 'connected'
  ).length;
  
  // Count connected communication platforms
  const commPlatformNames = ['Eva People for Web', 'Eva People for Teams', 'Eva People for Slack'];
  const connectedComms = isEvaPeopleActive ? 1 : 0; // Eva People for Web is the only live one
  const connectedCommsFromDB = configuredConnectors.filter(
    c => commPlatformNames.includes(c.name) && c.status === 'connected' && c.name !== 'Eva People for Web'
  ).length;
  const totalComms = connectedComms + connectedCommsFromDB;

  return (
    <PageShell data-testid="connectors-page">
      {/* Page Banner */}
      <PageBanner pageKey="connectors" />
      
      <PageHeader
        title="Connectors"
        description="Integrate Eva with your enterprise systems"
        actions={(
        <Button
          className="w-full sm:w-auto"
          onClick={() => window.location.href = 'mailto:support@vistry.ai?subject=Connector%20Integration%20Request'}
        >
          <Link2 className="w-4 h-4 mr-2" />
          Request Connector
        </Button>
        )}
      />

      <Card>
        <CardContent className="p-4 space-y-3">
          <h2 className="font-semibold">SharePoint imports</h2>
          <p className="text-sm text-muted-foreground">
            Import selected files as drafts for review. Copies use Eva access controls; SharePoint permissions, changes and deletions are not mirrored.
          </p>
          <Button variant="outline" onClick={() => handleConfigure('sharepoint')}>Connect with Microsoft</Button>
          <Button variant="link" onClick={() => { setSelectedConnectorType('sharepoint'); setSelectedConnector(null); setConfigDialogOpen(true); }}>Advanced IT setup with app credentials</Button>
          {configuredConnectors.filter(c => c.connector_type === 'sharepoint').map(c => (
            <div key={c.id} className="flex items-center justify-between gap-3">
              <span>{c.name} <Badge variant="secondary">{c.status}</Badge></span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleConfigure('sharepoint', c)}>Configure {c.name}</Button>
                <Button disabled={c.auth_mode === 'delegated' && c.status !== 'connected'} onClick={() => setLibraryConnector(c)}>Browse library</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Value Proposition Banner */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 border-0 text-white overflow-hidden">
        <CardContent className="p-4 sm:p-6 relative">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary-foreground/10 rounded-full blur-2xl" />
          <div className="absolute -right-5 -bottom-10 w-32 h-32 bg-primary-foreground/10 rounded-full blur-xl" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 relative">
            <div className="w-14 h-14 bg-primary-foreground/20 rounded-2xl flex items-center justify-center backdrop-blur">
              <Sparkles className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">Enterprise-Ready Integrations</h3>
              <p className="text-primary-foreground/80 text-sm mt-1 max-w-2xl">
                Connect Eva to your existing systems for personalized, context-aware HR assistance. 
                Sync knowledge from document repositories and employee data from HRIS for accurate, relevant responses.
              </p>
              <div className="flex items-center gap-2 mt-3 bg-primary-foreground/10 rounded-lg px-3 py-2 w-fit">
                <TrendingUp className="w-4 h-4 text-primary-foreground/80 shrink-0" />
                <p className="text-primary-foreground/80 text-xs">
                  <span className="font-semibold">Vote for integrations you need.</span> Upvote any coming-soon connector to signal demand — the most-voted integrations get built first.
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{activeCount}/{totalCount}</div>
              <div className="text-primary-foreground/60 text-sm">Connected</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connected Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard
          title="Active Connectors"
          value={activeCount}
          icon={Zap}
          color={activeCount > 0 ? 'success' : 'blue'}
        />
        <KpiCard
          title="Identity Provider"
          value={connectedIdentity}
          icon={Lock}
          color={connectedIdentity > 0 ? 'primary' : 'blue'}
        />
        <KpiCard
          title="Communication Platform"
          value={totalComms}
          icon={Cloud}
          color={totalComms > 0 ? 'warning' : 'blue'}
        />
      </div>

      {/* Connector Categories */}
      {CONNECTOR_CATEGORIES.map((category) => {
        const CategoryIcon = category.icon;
        const connectedInCategory = category.connectors.filter(c => {
          if (c.name === 'Eva People for Web') return isEvaPeopleActive;
          return c.status === 'connected';
        }).length;
        return (
          <section key={category.title}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-info/10 rounded-xl flex items-center justify-center">
                  <CategoryIcon className="w-4 h-4 text-info" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{category.title}</h2>
                  <p className="text-sm text-muted-foreground">{category.description}</p>
                </div>
              </div>
              {connectedInCategory > 0 && (
                <Badge className="bg-success/10 text-success">
                  {connectedInCategory} connected
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.connectors.map((connector) => {
                const voteData = connectorVotes.find(v => v.connector_name === connector.name);
                return (
                  <ConnectorCard 
                    key={connector.name} 
                    connector={connector} 
                    onConfigure={handleConfigure}
                    configuredConnectors={configuredConnectors}
                    evaPeopleConfig={evaPeopleConfig}
                    onEvaPeopleToggle={handleEvaPeopleToggle}
                    voteCount={voteData?.vote_count || 0}
                    userVoted={userVoted.includes(connector.name)}
                    onVote={handleVote}
                    votingConnector={votingConnector}
                  />
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Coming Soon Notice */}
      <Card className="bg-secondary dark:bg-card/50 border-border">
        <CardContent className="p-6 text-center">
          <Database className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-foreground">More Integrations Coming</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            We&apos;re constantly adding new connectors. Contact us to request a specific integration for your enterprise.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => window.location.href = 'mailto:support@vistry.ai?subject=Connector%20Integration%20Request'}
          >
            Request Integration
          </Button>
        </CardContent>
      </Card>

      {microsoftDialog && <MicrosoftSharePointDialog connector={microsoftDialog.connector} onClose={() => setMicrosoftDialog(null)} onSuccess={fetchConnectors} />}
      {libraryConnector && <SharePointLibraryDialog connector={libraryConnector} onClose={() => setLibraryConnector(null)} />}
      {/* Connector Configuration Dialog */}
      <ConnectorConfigDialog
        open={configDialogOpen}
        onOpenChange={setConfigDialogOpen}
        connectorType={selectedConnectorType}
        existingConnector={selectedConnector}
        onSuccess={handleConfigSuccess}
      />

      {/* Eva People Welcome Message Dialog */}
      <EvaPeopleWelcomeDialog
        open={welcomeDialogOpen}
        onOpenChange={setWelcomeDialogOpen}
        evaPeopleConfig={evaPeopleConfig}
        onSuccess={() => { fetchConnectors(); toast.success('Welcome message saved'); }}
      />
    </PageShell>
  );
}

function EvaPeopleWelcomeDialog({ open, onOpenChange, evaPeopleConfig, onSuccess }) {
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setWelcomeMessage(evaPeopleConfig?.config?.custom_welcome_message || evaPeopleConfig?.custom_welcome_message || '');
    }
  }, [open, evaPeopleConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/connectors/eva-people/config', {
        enabled: evaPeopleConfig?.enabled ?? true,
        custom_welcome_message: welcomeMessage,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error('Error saving', { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const DEFAULT_MESSAGE = "Hi! I'm Eva, your HR assistant. How can I help you today?";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Eva People — Welcome Message
          </DialogTitle>
          <DialogDescription>
            Customize the greeting message employees see when they open Eva People.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Label htmlFor="welcome-message">Welcome Message</Label>
          <textarea
            id="welcome-message"
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            placeholder={DEFAULT_MESSAGE}
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            data-testid="welcome-message-textarea"
          />
          <p className="text-xs text-muted-foreground">
            Leave blank to use the default: "{DEFAULT_MESSAGE}"
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-welcome-message-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
